import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { formatRelative } from '@/util/dates';
import type { RecallsState } from '@rock/types';
import { type FC, useMemo } from 'react';
import { untypedRecallCount } from './recallStatus';
import { useRecallJobRequest } from './useRecallJobRequest';

/**
 * Recalls the classifier could not file under a catalog type, grouped by the product kind it named — the
 * live list of catalog gaps. Every untyped recall that isn't rejected counts, so a recall approved through a
 * topic still names the gap. Counts come from `app/recalls-state`, recomputed by every sync and job, so this
 * tab reads one doc instead of scanning ten thousand recalls. A row opens the kind.
 */
export const NoMatchedTypeTab: FC<{ state: RecallsState | undefined }> = ({ state }) => {
    const onRowClick = useRowClick();
    const { request, isRequesting, canRequest } = useRecallJobRequest();
    const rows = useMemo(
        () => Object.entries(state?.untypedKindCounts ?? {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
        [state],
    );
    const total = untypedRecallCount(state);

    const handleReclassify = () =>
        request(
            { kind: 'reclassify' },
            'Reclassify',
            (jobId) =>
                `Job ${jobId} re-runs the classifier over every recall classified against an older catalog; a draft or live recall gets suggestions.`,
        );

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                    <CardTitle>By product kind</CardTitle>
                    <CardDescription>
                        {total === undefined ? (
                            'Counting…'
                        ) : (
                            <>
                                {total.toLocaleString()} untyped recall(s) in {rows.length} kind(s)
                                {state?.untypedKindCountsAt
                                    ? ` · counted ${formatRelative(state.untypedKindCountsAt)}`
                                    : ''}
                                .
                            </>
                        )}{' '}
                        Each row is a catalog gap, or a product we chose not to track.
                    </CardDescription>
                </div>
                <Button
                    variant="outline"
                    onClick={handleReclassify}
                    disabled={isRequesting || !canRequest}
                    title="Re-run the classifier over every recall classified against an older catalog: a classified recall takes the new answer (and may gain a type), a draft or live one gets suggestions (New suggestions on the Catalog tab)"
                >
                    {isRequesting ? 'Requesting…' : 'Reclassify stale'}
                </Button>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Kind</TableHead>
                            <TableHead className="text-right">Recalls</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2} className="py-8 text-center text-muted-foreground">
                                    {state ? 'No untyped recalls counted yet.' : 'Loading…'}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map(([kind, count]) => (
                            <TableRow
                                key={kind}
                                className="cursor-pointer"
                                onClick={onRowClick(`/products/recalls/no-matched-type/${encodeURIComponent(kind)}`)}
                            >
                                <TableCell className="font-medium">{kind}</TableCell>
                                <TableCell className="text-right tabular-nums">{count}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};
